/*
 * screen-socket-client.js  (ES5 + jQuery 3, load after terminal-pos.js)
 * Customer screens. Each screen follows exactly ONE POS device: the server binds it to
 * $_SESSION['deviceID'] when the token is issued, so a screen can never pick another POS.
 * One POS can drive several screens (max 10).
 *
 * ---- POS side: shares the POS socket (feature 'screen') ------------------------------
 *   SocketHub.ensurePos({ features: ['screen'] })          // or any call that includes it
 *   SocketHub.screen.publish(state)   last state wins. Calls within 30 ms are merged.
 *                                     The server keeps it, so a screen that opens later shows it at once.
 *                                     Kept while offline and sent again after reconnect.
 *                                     -> true if it goes out now, false if it waits for the connection
 *   SocketHub.screen.clear()          publish(null)
 *   SocketHub.screen.resetIfLeft()    for pages that never publish: clears the screen if an earlier page of
 *                                     this tab left a state on it (SocketUi calls it on every non-POS page)
 *   SocketHub.screen.event(name, data)  one-off (e.g. 'thank_you', 'show_qr'); not stored -> false if offline
 *   SocketHub.screen.count() / .list()  connected screens
 *   jQuery events on document:
 *     screen:presence  {count, screens}
 *     screen:input     {from, name, data}      something a screen sent (tip chosen, button pressed, ...)
 *     screen:error     msg
 *
 * ---- Screen side: its own socket, role 'screen' ----------------------------------------
 *   SocketHub.initScreen(opts) -> SocketHub.customerScreen
 *     opts (all optional): url, tokenUrl, screenId
 *     Started automatically on pages with <body is_customer_screen="1">.
 *   SocketHub.customerScreen.send(name, data)   -> its POS (screen:input there); false if offline
 *                           .requestState()  .connected()  .posOnline()  .stop()
 *   jQuery events on document:
 *     screen:ready   {id, pos, posOnline}
 *     screen:state   {state, seq}     render this (also fired right after every (re)connect)
 *     screen:event   {name, data}
 *     screen:pos     {online}         the POS this screen follows came / went
 *     screen:connection {online:false}
 *     screen:error   msg   (pos_offline for send(), ...)
 *     screen:fatal   {}    not logged in
 */
(function (window, $) {
  'use strict';
  var T = window.SocketHub,
    M = T.MSG;
  var MAX_STATE = 524288,
    COALESCE_MS = 30,
    ID_KEY = 'customerScreenId',
    SHOWN_KEY = 'screenStateShown';

  // Remembers (per tab) that the customer screen shows something from this POS, so a page that
  // cannot publish (settings, reports, ...) can send it back to idle after the user left the POS page.
  function markShown(on) {
    try {
      if (on) sessionStorage.setItem(SHOWN_KEY, '1');
      else sessionStorage.removeItem(SHOWN_KEY);
    } catch (e) {}
  }
  function wasShown() {
    try {
      return sessionStorage.getItem(SHOWN_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function fire(name, data) {
    $(document).trigger(name, [data]);
  }

  // ======================================================================================
  // POS side
  // ======================================================================================
  var last = null,
    hasLast = false,
    dirty = false,
    flushT = null,
    screenIds = [];

  function flushState() {
    flushT = null;
    if (!dirty || !T._posReady() || !T._posHas('screen')) return;
    if (T._posSend(M.SCREEN_STATE, { state: last })) dirty = false;
  }

  // The POS socket may be open for other features only (main, print, ...): add 'screen' on first use
  var ensured = false;
  function ensureScreen() {
    if (ensured || T._posHas('screen')) return;
    ensured = true;
    T.ensurePos({ features: ['screen'] }).fail(function (code) {
      ensured = false;
      console.warn('[screen] POS channel not started:', code);
    });
  }

  function presence() {
    fire('screen:presence', {
      count: screenIds.length,
      screens: screenIds.slice(),
    });
  }

  T.screen = {
    publish: function (state) {
      if (state === undefined) state = null;
      try {
        if (JSON.stringify(state).length > MAX_STATE) {
          console.warn('[screen] state too large, not sent');
          return false;
        }
      } catch (e) {
        console.warn('[screen] state is not serializable', e);
        return false;
      }
      ensureScreen();
      markShown(state !== null);
      last = state;
      hasLast = true;
      dirty = true;
      if (!flushT) flushT = setTimeout(flushState, COALESCE_MS);
      return T._posReady() && T._posHas('screen');
    },
    clear: function () {
      return T.screen.publish(null);
    },
    event: function (name, data) {
      ensureScreen();
      if (!T._posHas('screen') || !T._posReady()) return false;
      return T._posSend(M.SCREEN_EVENT, {
        name: String(name),
        data: data === undefined ? null : data,
      });
    },
    // Call on pages that never publish: if an earlier page left something on the screen, reset it
    resetIfLeft: function () {
      if (!wasShown()) return false;
      T.screen.clear();
      return true;
    },
    count: function () {
      return screenIds.length;
    },
    list: function () {
      return screenIds.slice();
    },
  };

  T._posModule('screen', {
    onReady: function (w) {
      screenIds = $.isArray(w.screens) ? $.map(w.screens, String) : [];
      presence();
      if (hasLast) {
        dirty = true; // the server may have restarted: send what we show now
        flushState();
      }
    },
    onMessage: function (m) {
      switch (m.type) {
        case M.SCREEN_PRESENCE:
          screenIds = $.isArray(m.screens) ? $.map(m.screens, String) : [];
          presence();
          break;
        case M.SCREEN_INPUT:
          fire('screen:input', { from: m.from, name: m.name, data: m.data });
          break;
        case M.SCREEN_ERROR:
          fire('screen:error', m);
          break;
      }
    },
    onClose: function () {
      screenIds = [];
      presence();
    },
    onStop: function () {
      screenIds = [];
      presence();
    },
    busy: function () {
      return false;
    },
  });

  // ======================================================================================
  // Screen side
  // ======================================================================================
  var ss = null,
    posOnline = false;

  function storedScreenId() {
    var id = null;
    try {
      id = sessionStorage.getItem(ID_KEY); // per tab: two screens in one browser get two ids
    } catch (e) {}
    return id || freshScreenId();
  }
  function freshScreenId() {
    var id = T.newId();
    try {
      sessionStorage.setItem(ID_KEY, id);
    } catch (e) {}
    return id;
  }

  T.initScreen = function (opts) {
    opts = opts || {};
    if (ss) return T.customerScreen;

    function build(id) {
      ss = T.createSocket({
        url: opts.url || T.config.url(),
        tokenUrl: opts.tokenUrl || T.config.tokenUrl,
        role: 'screen',
        deviceId: id,
        organizerId: opts.organizerId || T.config.organizerId,
        onReady: function (w) {
          posOnline = !!w.posOnline;
          fire('screen:ready', { id: w.id, pos: w.pos, posOnline: posOnline });
          fire('screen:pos', { online: posOnline });
          fire('screen:state', {
            state: w.state === undefined ? null : w.state,
            seq: w.seq || 0,
          });
        },
        onMessage: function (m) {
          switch (m.type) {
            case M.SCREEN_STATE:
              fire('screen:state', {
                state: m.state === undefined ? null : m.state,
                seq: m.seq || 0,
              });
              break;
            case M.SCREEN_EVENT:
              fire('screen:event', { name: m.name, data: m.data });
              break;
            case M.SCREEN_POS:
              posOnline = !!m.online;
              fire('screen:pos', { online: posOnline });
              break;
            case M.SCREEN_ERROR:
              fire('screen:error', m);
              break;
          }
        },
        onClose: function () {
          posOnline = false;
          fire('screen:connection', { online: false });
        },
        onFatal: function () {
          fire('screen:fatal', {});
        },
        onReplaced: function () {
          // The server names a screen by its session device id, so a new socket would be replaced
          // again: give way to the newer tab. After a plain reload the old page is gone, so nothing runs here.
          ss.destroy();
          posOnline = false;
          fire('screen:connection', { online: false, replaced: true });
        },
      });
    }

    build(opts.screenId || storedScreenId());
    ss.start();

    T.customerScreen = {
      send: function (name, data) {
        return (
          !!ss &&
          ss.send(M.SCREEN_INPUT, {
            name: String(name),
            data: data === undefined ? null : data,
          })
        );
      },
      requestState: function () {
        return !!ss && ss.send(M.SCREEN_REQUEST_STATE, {});
      },
      connected: function () {
        return !!ss && ss.isReady();
      },
      posOnline: function () {
        return posOnline;
      },
      stop: function () {
        if (ss) ss.destroy();
        ss = null;
        posOnline = false;
      },
    };
    return T.customerScreen;
  };

  // ---- boot: customer screen pages connect by themselves -------------------------------
})(window, jQuery);
